import { FormHelperText } from "@material-ui/core";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { shallowEqual, useSelector } from "react-redux";
import Select from "react-select";
import { Card, CardBody, CardHeader } from "../../_metronic/_partials/controls";
import { getAnswers } from "../../api/answer";
import { getGames } from "../../api/game";
import { getGroupsByGame } from "../../api/group";
import { getQuizzes } from "../../api/quiz";
import { getScenes } from "../../api/scene";
import { alertError } from "../../utils/logger";
import {
  QuizResultsTable,
  ResultsTable,
  resultFormatter,
} from "../components/tables/table";

/** ======= CONFIG ======= */
const POLL_MS = 5000; // intervalo de refresco (ms)

/** ------- ORDENACIÓN --------- */
/** Orden en tabla /results: Cancún / ONCOnext. Cada índice incluye alias (p. ej. nombres musicales antiguos). */
const SCENARIO_NAME_BUCKETS = [
  ["Sus inicios"],
  ["Evolución"],
  ["Coca-Cola"],
  ["Inversión"],
];
const normalizeScenarioName = (name) =>
  String(name || "")
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/\s+/g, " ")
    .trim();

const ORDER_INDEX_BY_NAME = new Map();
SCENARIO_NAME_BUCKETS.forEach((aliases, idx) => {
  for (const name of aliases) {
    ORDER_INDEX_BY_NAME.set(normalizeScenarioName(name), idx);
  }
});

const scenarioRank = (name) => {
  const normalized = normalizeScenarioName(name);
  // Match exacto primero
  if (ORDER_INDEX_BY_NAME.has(normalized)) return ORDER_INDEX_BY_NAME.get(normalized);
  // Fallback tolerante: permite sufijos/prefijos en nombre de escenario
  for (const [orderedName, idx] of ORDER_INDEX_BY_NAME.entries()) {
    if (normalized.includes(orderedName) || orderedName.includes(normalized)) {
      return idx;
    }
  }
  return Number.MAX_SAFE_INTEGER;
};
function sortScenarios(scenarios) {
  return [...(scenarios || [])].sort((a, b) => {
    const ra = scenarioRank(a.fullName);
    const rb = scenarioRank(b.fullName);
    if (ra !== rb) return ra - rb;
    const ao = a.order ?? a.position ?? a.index ?? a.sequence ?? Number.MAX_SAFE_INTEGER;
    const bo = b.order ?? b.position ?? b.index ?? b.sequence ?? Number.MAX_SAFE_INTEGER;
    if (ao !== bo) return ao - bo;
    return (a.fullName || "").localeCompare(b.fullName || "");
  });
}
function sortQuizzesForScenario(quizzes, scenarioId) {
  return (quizzes || [])
    .filter((q) => q.scenario?._id === scenarioId && !q.supertest)
    .sort((a, b) => {
      const ao = a.order ?? a.position ?? a.index ?? a.sequence ?? Number.MAX_SAFE_INTEGER;
      const bo = b.order ?? b.position ?? b.index ?? b.sequence ?? Number.MAX_SAFE_INTEGER;
      if (ao !== bo) return ao - bo;
      return (a.fullName || "").localeCompare(b.fullName || "");
    });
}

/** ------- UTILS -------- */
const idsOf = (arr) => (arr || []).map((x) => x?._id);
const arraysEqual = (a, b) => a.length === b.length && a.every((v, i) => v === b[i]);

/** ------- CONSTRUCCIÓN DE DATOS --------- */
function getGameQuizzesData(groups, scenarios, quizzes, answers) {
  const ordered = scenarios || [];
  let data = [];
  ordered.forEach((scenario) => {
    const quizzesOrdered = sortQuizzesForScenario(quizzes, scenario._id);
    const quizzesRes = quizzesOrdered.map((quiz) => {
      const quizRes = { quiz: quiz.fullName };
      (groups || []).forEach((group, indexGroup) => {
        const answer = (answers || []).find(
          (a) => a.quiz === quiz._id && a.group === group._id
        );
        if (answer) {
          quizRes[`group${indexGroup + 1}`] = {
            pts: answer.score,
            time: answer.time,
            name: group.fullName,
            correct: answer.correct,
          };
        }
      });
      return quizRes;
    });
    data.push({ scenario: scenario.fullName, results: quizzesRes });
  });
  return data;
}

function buildTotalsRow(groups, scenarios, answers) {
  const totals = {
    id: "TOTAL",
    scenarioId: "TOTAL",
    scenario: "TOTAL",
    scenarioName: "Tiempo total",
  };
  const scenarioIds = new Set((scenarios || []).map((s) => s._id));
  (groups || []).forEach((g, idx) => {
    const totalPts = (answers || [])
      .filter((a) => a.group === g._id && scenarioIds.has(a.scenario))
      .reduce((sum, a) => sum + (a.score || 0), 0);
    totals[`group${idx + 1}`] = {
      pts: totalPts,
      time: g?.finished ? g.time : null,
      name: g.fullName,
      finished: !!g?.finished,
    };
  });
  return totals;
}

function getGameData(groups, scenarios, answers) {
  let data = [];
  (scenarios || []).forEach((scenario, indexScenario) => {
    let elem = {
      id: scenario._id,            // clave estable
      scenarioId: scenario._id,    // referencia para detalles
      scenario: `Scenario ${indexScenario + 1}`,
      scenarioName: scenario.fullName,
    };
    (groups || []).forEach((group, indexGroup) => {
      const groupAnswers = (answers || []).filter(
        (a) => a.scenario === scenario._id && a.group === group._id
      );
      if (groupAnswers.length) {
        const result = groupAnswers.reduce(
          (acc, curr) => ({ score: (acc.score || 0) + (curr.score || 0), time: curr.time }),
          { score: 0, time: null }
        );
        elem[`group${indexGroup + 1}`] = {
          pts: result.score,
          time: result.time,
          name: group.fullName,
        };
      }
    });
    data.push(elem);
  });
  return data;
}

export function ResultsPage() {
  const [games, setGames] = useState([]);
  const [groups, setGroups] = useState([]);
  const [quizzes, setQuizzes] = useState([]);
  const [answers, setAnswers] = useState([]);
  const [scenarios, setScenarios] = useState([]);
  const [quizzesData, setQuizzesData] = useState([]);
  const [actualGame, setActualGame] = useState({ fullName: "" });
  const [data, setData] = useState([]);
  const [expandedRows, setExpandedRows] = useState([]); // ids de escenario
  const [isPaused, setIsPaused] = useState(false);      // ⬅️ Pause/Play

  const user = useSelector((store) => store.authentication?.user, shallowEqual);

  const isMountedRef = useRef(true);
  const timerRef = useRef(null);

  useEffect(() => () => {
    isMountedRef.current = false;
    if (timerRef.current) clearInterval(timerRef.current);
  }, []);

  /** 1) Cargar juegos y preseleccionar (usuario o primero) */
  useEffect(() => {
    (async () => {
      try {
        const res = await getGames();
        if (res.status === 200) {
          const list = res.data || [];
          setGames(list);
          const chosen =
            (user?.game && list.find((g) => g._id === user.game)) ||
            list[0] ||
            null;
          if (chosen) setActualGame(chosen);
        }
      } catch (error) {
        alertError({ error, customMessage: "Could not get games." });
      }
    })();
  }, [user?.game]);

  /** 2) Polling de datos del juego seleccionado con Pause/Play */
  useEffect(() => {
    if (!actualGame?._id) return;

    const tick = async () => {
      try {
        const [resGroups, resQuizzes, resAnswers, resScenes] = await Promise.all([
          getGroupsByGame(actualGame._id),
          getQuizzes(),
          getAnswers(),
          getScenes(),
        ]);
        if (
          resGroups.status === 200 &&
          resQuizzes.status === 200 &&
          resAnswers.status === 200 &&
          resScenes.status === 200
        ) {
          const groupsL = resGroups.data || [];
          const quizzesL = resQuizzes.data || [];
          const answersL = resAnswers.data || [];
          const scenariosOrdered = sortScenarios(resScenes.data || []);

          // Sólo setear si cambiaron IDs (evita re-montajes innecesarios)
          const setIfChanged = (setter, prev, next, keyFn = idsOf) => {
            const prevK = keyFn(prev);
            const nextK = keyFn(next);
            if (!arraysEqual(prevK, nextK)) setter(next);
          };

          if (isMountedRef.current) {
            setIfChanged(setGroups, groups, groupsL);
            setIfChanged(setScenarios, scenarios, scenariosOrdered);
            setQuizzes(quizzesL);
            setAnswers(answersL);

            // Construcción de tablas
            const quizzesDataL = getGameQuizzesData(groupsL, scenariosOrdered, quizzesL, answersL);
            const gameData = getGameData(groupsL, scenariosOrdered, answersL);
            const totalsRow = buildTotalsRow(groupsL, scenariosOrdered, answersL);

            setData([...gameData, totalsRow]);
            setQuizzesData(quizzesDataL);

            // Mantener estado expandido: elimina ids que ya no existan
            const validIds = new Set((scenariosOrdered || []).map((s) => s._id));
            setExpandedRows((prev) => prev.filter((id) => validIds.has(id)));
          }
        }
      } catch (error) {
        alertError({ error, customMessage: "Polling error while fetching data." });
      }
    };

    // Limpia cualquier intervalo previo
    if (timerRef.current) clearInterval(timerRef.current);

    if (!isPaused) {
      // Primer fetch inmediato y luego intervalos
      tick();
      timerRef.current = setInterval(tick, POLL_MS);
    }

    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [actualGame?._id, isPaused]); // se reactiva al cambiar juego o al reanudar

  /** ======= UI helpers ======= */
  const groupHeader = (g, idx) => (
    <div style={{ display: "flex", flexDirection: "column", alignItems: "center", lineHeight: 1.1 }}>
      <div>{g?.fullName || `Group ${idx + 1}`}</div>
      <small style={{ opacity: 0.75 }}>{g?.tableNum ? `Table ${g.tableNum}` : "Table -"}</small>
    </div>
  );

  function styleFormatter(cell, row) {
    if (!cell) return;
    if (row?.scenario === "TOTAL") return; // sin colores en la fila total
    let max = true, min = true;
    const totalGroups = Math.min(groups.length, 30);
    for (let i = 1; i <= totalGroups; ++i) {
      const gi = row[`group${i}`];
      if (gi?.pts > cell.pts || (gi?.pts === cell.pts && gi?.time < cell.time)) max = false;
      if (gi?.pts < cell.pts || (gi?.pts === cell.pts && gi?.time > cell.time)) min = false;
      if (!min && !max) return;
    }
    if (max) return { backgroundColor: "#c8e6c9" };
    else if (min) return { backgroundColor: "#e6c8c8" };
  }

  // Clave estable a usar por la tabla
  const keyField = "scenarioId";

  // Control de expansión por id estable
  const handleOnExpandRow = (row, isExpand) => {
    const key = row?.[keyField] || row?.id;
    setExpandedRows((prev) =>
      isExpand ? [...new Set([...prev, key])] : prev.filter((x) => x !== key)
    );
  };

  // Memo de columnas por ids de grupos (evita recrearlas en cada tick)
  const groupsKey = useMemo(() => JSON.stringify(idsOf(groups)), [groups]);
  const columns = useMemo(() => {
    const base = [{ dataField: "scenarioName", text: "Scenario" }];
    const groupCols = groups.slice(0, 30).map((g, idx) => ({
      dataField: `group${idx + 1}`,
      text: `Group ${idx + 1}`,
      headerAlign: "center",
      headerFormatter: () => groupHeader(g, idx),
      formatter: resultFormatter,
      style: styleFormatter,
      headerStyle: () => ({ whiteSpace: "normal" }),
    }));
    return [...base, ...groupCols];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [groupsKey]);

  const quizColumns = useMemo(() => {
    const base = [{ dataField: "quiz", text: "Quiz", headerAlign: "center" }];
    const groupCols = groups.slice(0, 30).map((g, idx) => ({
      dataField: `group${idx + 1}`,
      text: `Group ${idx + 1}`,
      headerAlign: "center",
      headerFormatter: () => groupHeader(g, idx),
      formatter: resultFormatter,
      headerStyle: () => ({ whiteSpace: "normal" }),
    }));
    return [...base, ...groupCols];
  }, [groupsKey]);

  // Header con botón Pause/Play arriba a la derecha
  const header = (
    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
      <h4
        style={{
          margin: 0,
          color: "rgb(24, 28, 50)",
          borderBottom: "2px solid rgb(215, 218, 231)",
          padding: "0.5em 0",
        }}
      >
        Results
      </h4>
      <button
        type="button"
        className={`btn btn-sm ${isPaused ? "btn-success" : "btn-light-primary"}`}
        onClick={() => setIsPaused((p) => !p)}
        title={isPaused ? "Resume auto-refresh" : "Pause auto-refresh"}
      >
        {isPaused ? "▶ Play" : "⏸ Pause"}
      </button>
    </div>
  );

  // expandRow controlado y memoizado
  const expandRow = useMemo(() => ({
    renderer: (row) => {
      if (row.scenario === "TOTAL") return null;
      const idx = scenarios.findIndex((s) => s._id === row.scenarioId);
      return (
        <div style={{ margin: "10px" }}>
          <b>{`${row.scenarioName} | Quizzes Results`}</b>
          <QuizResultsTable data={quizzesData[idx]?.results} columns={quizColumns} />
        </div>
      );
    },
    showExpandColumn: true,
    onlyOneExpanding: true,
    expanded: expandedRows,
    onExpand: handleOnExpandRow,
    expandHeaderColumnRenderer: ({ isAnyExpands }) => (isAnyExpands ? <b>-</b> : <b>+</b>),
    expandColumnRenderer: ({ expanded }) => (expanded ? <b>-</b> : <b>...</b>),
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }), [expandedRows, scenarios, quizzesData, quizColumns]);

  /** ======= RENDER ======= */
  return (
    <>
      <Card>
        <CardHeader title={actualGame?.fullName || "Game results"} />
        <CardBody>
          <Select
            options={games?.map((game) => ({ label: game.fullName, value: game._id }))}
            value={
              actualGame?._id
                ? { label: actualGame.fullName, value: actualGame._id }
                : null
            }
            onChange={(opt) => {
              const next = games.find((g) => g._id === opt?.value);
              if (next) {
                setExpandedRows([]); // opcional: limpiar al cambiar de juego
                setActualGame(next);
              }
            }}
            isClearable
            isSearchable
            placeholder="Select game..."
            className="mb-3 mt-3"
          />
          <FormHelperText>
            {isPaused ? "Paused — no auto-refresh" : "Auto-refresh enabled"}
          </FormHelperText>

          {actualGame?._id && (
            <ResultsTable
              keyField="scenarioId"
              data={data}
              columns={columns}
              header={header}
              expandRow={expandRow}
            />
          )}
        </CardBody>
      </Card>
    </>
  );
}
